/**
 * PasswordProtection Web Component
 *
 * Responsibilities:
 * - Validate user-entered password against the value provided via data attribute
 * - Persist successful entry to localStorage for subsequent visits
 * - Provide a loading state ("Granting you access…") before removing the gate element
 * - Handle errors using native form validation
 *
 */
class PasswordProtection extends HTMLElement {
  constructor() {
    super();
    this.onSubmit = this.onSubmit.bind(this);
    this.onInput = this.onInput.bind(this);
    this.onToggleVisibility = this.onToggleVisibility.bind(this);
    this.onToggleView = this.onToggleView.bind(this);
    this.onKlaviyoSubmit = this.onKlaviyoSubmit.bind(this);
  }

  connectedCallback() {
    // Extract the expected password hash (sha256 hex) from the component's data attribute
    this.expectedHashHex = (this.dataset.passwordProtection || '').trim().toLowerCase();
    this.displayMode = this.dataset.displayMode || 'email-password';
    
    this.form = this.querySelector('.password-protection__form');
    this.passwordInput = this.form ? this.form.querySelector('input[name="password"]') : null;
    this.submitButton = this.form ? this.form.querySelector('input[type="submit"]') : null;
    this.visibilityToggle = this.form ? this.form.querySelector('.password-protection__toggle') : null;
    this.earlyAccessSection = this.querySelector('.password-protection__early-access');
    this.toggleViewLinks = this.querySelectorAll('.password-protection__toggle-view');

    // If access was previously granted, remove immediately to avoid UI flash
    if (this.isAccessGranted()) {
      this.removeBodyLock();
      Promise.resolve().then(() => this.remove());
      return;
    }

    // Access not granted: lock body scroll while the gate is visible
    this.addBodyLock();

    if (this.form && this.passwordInput) {
      this.form.addEventListener('submit', this.onSubmit);
      this.passwordInput.addEventListener('input', this.onInput);
      if (this.visibilityToggle) {
        this.visibilityToggle.addEventListener('click', this.onToggleVisibility);
        var isPassword = this.passwordInput.type === 'password';
        this.visibilityToggle.setAttribute('aria-pressed', isPassword ? 'false' : 'true');
        this.visibilityToggle.setAttribute('aria-label', isPassword ? 'Show password' : 'Hide password');
      }

      if (this.earlyAccessSection) {
        this.passwordInput.removeAttribute('required');
      } else if (typeof this.passwordInput.focus === 'function') {
        this.passwordInput.focus();
      }
    }

    this.toggleViewLinks.forEach((link) => {
      link.addEventListener('click', this.onToggleView);
    });

    this.klaviyoForm = this.querySelector('.password-protection__early-access-form');
    if (this.klaviyoForm) {
      this.klaviyoForm.addEventListener('submit', this.onKlaviyoSubmit);
    }
  }

  disconnectedCallback() {
    if (this.form) {
      this.form.removeEventListener('submit', this.onSubmit);
    }
    if (this.passwordInput) {
      this.passwordInput.removeEventListener('input', this.onInput);
    }
    if (this.visibilityToggle) {
      this.visibilityToggle.removeEventListener('click', this.onToggleVisibility);
    }
    if (this.toggleViewLinks) {
      this.toggleViewLinks.forEach((link) => {
        link.removeEventListener('click', this.onToggleView);
      });
    }
    if (this.klaviyoForm) {
      this.klaviyoForm.removeEventListener('submit', this.onKlaviyoSubmit);
    }
    // Ensure body is unlocked on disconnect
    this.removeBodyLock();
  }

  // Use pathname to scope access per-route so that there can be multiple password protection pages at the same time.
  storageKey() {
    return 'password-protection:' + window.location.pathname;
  }

  isAccessGranted() {
    try {
      var stored = window.localStorage.getItem(this.storageKey());
      if (!stored) return false;
      if (this.displayMode === 'email-only') {
        return stored === 'email-granted';
      }
      if (!this.expectedHashHex) return false;
      return stored.toLowerCase() === this.expectedHashHex;
    } catch (err) {
      // Storage may be unavailable (privacy mode, blocked). Treat as not granted.
      return false;
    }
  }

  setLoading(loading) {
    if (!this.submitButton) return;
    if (loading) {
      this._prevButtonValue = this.submitButton.value;
      this.submitButton.value = 'Granting you access…';
      this.submitButton.disabled = true;
      this.submitButton.setAttribute('aria-busy', 'true');
    } else {
      if (typeof this._prevButtonValue === 'string') {
        this.submitButton.value = this._prevButtonValue;
      }
      this.submitButton.disabled = false;
      this.submitButton.removeAttribute('aria-busy');
    }
  }

  clearValidity() {
    if (!this.passwordInput) return;
    // Clear any previous error message
    this.passwordInput.setCustomValidity('');
    this.passwordInput.removeAttribute('aria-invalid');
  }

  onInput() {
    this.clearValidity();
  }

  onToggleVisibility() {
    if (!this.passwordInput || !this.visibilityToggle) return;
    var isCurrentlyPassword = this.passwordInput.type === 'password';
    this.passwordInput.type = isCurrentlyPassword ? 'text' : 'password';
    var pressed = isCurrentlyPassword;
    this.visibilityToggle.setAttribute('aria-pressed', pressed ? 'true' : 'false');
    this.visibilityToggle.setAttribute('aria-label', pressed ? 'Hide password' : 'Show password');
    if (typeof this.passwordInput.focus === 'function') {
      this.passwordInput.focus();
    }
  }

  onKlaviyoSubmit(event) {
    event.preventDefault();
    var self = this;
    var form = this.klaviyoForm;
    var emailInput = form.querySelector('input[type="email"]');
    var messagesContainer = form.querySelector('.password-protection__klaviyo_messages');
    var errorMessage = form.querySelector('.klaviyo-error-message');
    var successMessage = form.querySelector('.klaviyo-success-message');
    var listId = form.dataset.klaviyoListId;
    var email = (emailInput.value || '').trim();
    var validEmailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    // Reset messages
    messagesContainer.hidden = true;
    errorMessage.hidden = true;
    errorMessage.textContent = '';
    successMessage.hidden = true;

    if (!email || !email.match(validEmailRegex)) {
      errorMessage.textContent = 'Please enter a valid email address.';
      errorMessage.hidden = false;
      messagesContainer.hidden = false;
      emailInput.focus();
      return;
    }

    if (!listId) {
      errorMessage.textContent = 'Signup is not configured.';
      errorMessage.hidden = false;
      messagesContainer.hidden = false;
      return;
    }

    var submitButtonContainer = form.querySelector('.password-protection__early-access-submit');
    var submitButton = form.querySelector('input[type="submit"]');
    if (submitButton) submitButton.disabled = true;

    // Build payload from form fields
    var fieldsArr = ['Accepts Marketing'];
    var fieldsObj = { 'Accepts Marketing': true };
    form.querySelectorAll('input').forEach(function (input) {
      if (input.type !== 'submit' && input.type !== 'hidden' && input.type !== 'email' && input.name !== '$consent' && input.type !== 'checkbox') {
        fieldsArr.push(input.name);
        fieldsObj[input.name] = input.value;
      }
      if (input.name === '$source') {
        fieldsArr.push('$source');
        fieldsObj['$source'] = input.value;
      }
    });

    var payload = { g: listId, email: email, $fields: fieldsArr };
    for (var key in fieldsObj) {
      payload[key] = fieldsObj[key];
    }

    var formData = new URLSearchParams();
    for (var k in payload) {
      if (Array.isArray(payload[k])) {
        payload[k].forEach(function (v) { formData.append(k, v); });
      } else {
        formData.append(k, payload[k]);
      }
    }

    fetch('https://manage.kmail-lists.com/ajax/subscriptions/subscribe', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: formData.toString()
    })
      .then(function (response) { return response.json(); })
      .then(function (data) {
        if (data.success) {
          if (self.displayMode === 'email-only') {
            if (submitButton) {
              submitButton.value = 'Granting you access…';
              submitButton.disabled = true;
              submitButton.setAttribute('aria-busy', 'true');
            }
            try {
              window.localStorage.setItem(self.storageKey(), 'email-granted');
            } catch (err) {}
            window.setTimeout(function () {
              self.removeBodyLock();
              self.remove();
            }, 600);
          } else {
            if (submitButton) submitButton.disabled = false;
            successMessage.hidden = false;
            messagesContainer.hidden = false;
            emailInput.closest('.password-protection__early-access-field').hidden = true;
            if (submitButtonContainer) submitButtonContainer.hidden = true;
          }
        } else {
          if (submitButton) submitButton.disabled = false;
          var errors = data.errors || ['Subscription failed.'];
          errorMessage.textContent = errors.join('. ');
          errorMessage.hidden = false;
          messagesContainer.hidden = false;
        }
      })
      .catch(function () {
        if (submitButton) submitButton.disabled = false;
        if (submitButtonContainer) submitButtonContainer.hidden = false;
        errorMessage.textContent = 'Something went wrong. Please try again.';
        errorMessage.hidden = false;
        messagesContainer.hidden = false;
      });
  }

  onToggleView(event) {
    event.preventDefault();
    var self = this;
    var action = event.currentTarget.dataset.toggle;
    var toHide = action === 'show-password' ? this.earlyAccessSection : this.form;
    var toShow = action === 'show-password' ? this.form : this.earlyAccessSection;

    if (toHide) {
      toHide.style.opacity = '0';
      toHide.style.transition = 'opacity 0.3s ease';
    }

    setTimeout(function () {
      if (toHide) {
        toHide.hidden = true;
        toHide.style.opacity = '';
        toHide.style.transition = '';
      }
      if (toShow) {
        toShow.style.opacity = '0';
        toShow.hidden = false;
        // Force reflow so the browser registers opacity:0 before transitioning
        toShow.offsetHeight;
        toShow.style.transition = 'opacity 0.3s ease';
        toShow.style.opacity = '1';
      }

      if (action === 'show-password' && self.passwordInput) {
        self.passwordInput.setAttribute('required', '');
        self.clearValidity();
        if (typeof self.passwordInput.focus === 'function') {
          self.passwordInput.focus();
        }
      } else if (action === 'show-klaviyo' && self.passwordInput) {
        self.passwordInput.removeAttribute('required');
        self.clearValidity();
      }
    }, 300);
  }

  addBodyLock() {
    var body = document.body;
    if (!body) return;
    if (!body.classList.contains('fancybox-lock')) {
      body.classList.add('fancybox-lock');
    }
  }

  removeBodyLock() {
    var body = document.body;
    if (!body) return;
    if (body.classList.contains('fancybox-lock')) {
      body.classList.remove('fancybox-lock');
    }
  }

  onSubmit(event) {
    event.preventDefault();
    if (!this.passwordInput) return;

    var userValue = (this.passwordInput.value || '').trim();

    if (!userValue) {
      this.passwordInput.setCustomValidity('Please enter a password.');
      this.passwordInput.setAttribute('aria-invalid', 'true');
      if (typeof this.passwordInput.reportValidity === 'function') {
        this.passwordInput.reportValidity();
      } else if (this.form && typeof this.form.reportValidity === 'function') {
        this.form.reportValidity();
      }
      return;
    }

    // Compare hash(userValue) against expected hash
    this.hashSHA256(userValue)
      .then((hashHex) => {
        var normalized = (hashHex || '').toLowerCase();
        if (normalized === this.expectedHashHex) {
          this.setLoading(true);
          // Persist success; do not block UX if storage fails
          try {
            window.localStorage.setItem(this.storageKey(), normalized);
          } catch (err) {
          }

          // Provide a brief loading experience before removal
          window.setTimeout(() => {
            this.setLoading(false);
            this.removeBodyLock();
            this.remove();
          }, 600);
        } else {
          this.passwordInput.setCustomValidity('Incorrect password. Please try again.');
          this.passwordInput.setAttribute('aria-invalid', 'true');
          if (typeof this.passwordInput.reportValidity === 'function') {
            this.passwordInput.reportValidity();
          } else if (this.form && typeof this.form.reportValidity === 'function') {
            this.form.reportValidity();
          }
        }
      })
      .catch(() => {
        // Hashing not supported or failed
        this.passwordInput.setCustomValidity('Your browser cannot verify the password.');
        this.passwordInput.setAttribute('aria-invalid', 'true');
        if (typeof this.passwordInput.reportValidity === 'function') {
          this.passwordInput.reportValidity();
        } else if (this.form && typeof this.form.reportValidity === 'function') {
          this.form.reportValidity();
        }
      });
  }
}


// Countdown Timer Element
var CountdownTimer = class extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.endDate = new Date(this.getAttribute('end-date'));
    
    // Extract label attributes for singular/plural handling
    this.labels = {
      days: {
        singular: this.getAttribute('days-label') || 'day',
        plural: this.getAttribute('days-label-plural') || 'days'
      },
      hours: {
        singular: this.getAttribute('hours-label') || 'hour',
        plural: this.getAttribute('hours-label-plural') || 'hours'
      },
      minutes: {
        singular: this.getAttribute('minutes-label') || 'minute',
        plural: this.getAttribute('minutes-label-plural') || 'minutes'
      },
      seconds: {
        singular: this.getAttribute('seconds-label') || 'second',
        plural: this.getAttribute('seconds-label-plural') || 'seconds'
      }
    };
    
    this.initializeClock();
  }

  getTimeRemaining() {
    const t = Date.parse(this.endDate) - Date.parse(new Date());
    const seconds = Math.floor((t / 1000) % 60);
    const minutes = Math.floor((t / 1000 / 60) % 60);
    const hours = Math.floor((t / (1000 * 60 * 60)) % 24);
    const days = Math.floor(t / (1000 * 60 * 60 * 24));
    return {
      total: t,
      days: days,
      hours: hours,
      minutes: minutes,
      seconds: seconds
    };
  }

  getLabel(count, labelObj) {
    return count === 1 ? labelObj.singular : labelObj.plural;
  }

  initializeClock() {
    const daysSpan = this.querySelector('#days');
    const hoursSpan = this.querySelector('#hours');
    const minutesSpan = this.querySelector('#minutes');
    const secondsSpan = this.querySelector('#seconds');
    
    const daysLabel = daysSpan?.parentElement.querySelector('.countdown__label');
    const hoursLabel = hoursSpan?.parentElement.querySelector('.countdown__label');
    const minutesLabel = minutesSpan?.parentElement.querySelector('.countdown__label');
    const secondsLabel = secondsSpan?.parentElement.querySelector('.countdown__label');

    const updateClock = () => {
      const t = this.getTimeRemaining();

      if (t.total <= 0) {
        clearInterval(this.timeinterval);
        // Set all values to 0 when countdown expires
        daysSpan.textContent = 0;
        hoursSpan.textContent = 0;
        minutesSpan.textContent = 0;
        secondsSpan.textContent = 0;
        
        // Update labels for zero values (use plural for zero)
        if (daysLabel) daysLabel.textContent = this.labels.days.plural;
        if (hoursLabel) hoursLabel.textContent = this.labels.hours.plural;
        if (minutesLabel) minutesLabel.textContent = this.labels.minutes.plural;
        if (secondsLabel) secondsLabel.textContent = this.labels.seconds.plural;
      } else {
        // Update time values
        daysSpan.textContent = t.days;
        hoursSpan.textContent = t.hours;
        minutesSpan.textContent = t.minutes;
        secondsSpan.textContent = t.seconds;
        
        // Update labels with proper singular/plural forms
        if (daysLabel) daysLabel.textContent = this.getLabel(t.days, this.labels.days);
        if (hoursLabel) hoursLabel.textContent = this.getLabel(t.hours, this.labels.hours);
        if (minutesLabel) minutesLabel.textContent = this.getLabel(t.minutes, this.labels.minutes);
        if (secondsLabel) secondsLabel.textContent = this.getLabel(t.seconds, this.labels.seconds);
      }
    };

    updateClock();
    this.timeinterval = setInterval(updateClock, 1000);
  }
}

if (!window.customElements.get("countdown-timer")) {
  window.customElements.define("countdown-timer", CountdownTimer);
}

if (!window.customElements.get('password-protection')) {
  window.customElements.define('password-protection', PasswordProtection);
}

// Helper methods for hashing with Web Crypto
PasswordProtection.prototype.hashSHA256 = function (text) {
  var encoder = new TextEncoder();
  var data = encoder.encode(text);
  var subtle = (window.crypto && window.crypto.subtle) || (window.msCrypto && window.msCrypto.subtle);
  if (!subtle || !subtle.digest) {
    return Promise.reject(new Error('SubtleCrypto not available'));
  }
  return Promise.resolve(subtle.digest('SHA-256', data)).then((hashBuffer) => {
    return PasswordProtection.prototype.bufferToHex(hashBuffer);
  });
};

PasswordProtection.prototype.bufferToHex = function (buffer) {
  var bytes = new Uint8Array(buffer);
  var hex = '';
  for (var i = 0; i < bytes.length; i++) {
    var h = bytes[i].toString(16);
    if (h.length === 1) h = '0' + h;
    hex += h;
  }
  return hex;
};
